const PARTICLE_SIZE = 3;
const GRAVITY = 800;
const BASE_LIFE = 600;
const LIFE_VARIANCE = 200;
const WAVE_DELAY = 200;
const RANDOM_DELAY = 100;

/**
 * Effet de desintegration en particules style Telegram.
 * Capture l'element DOM dans un canvas, le decompose en particules
 * qui s'envolent avec gravite et fade-out.
 *
 * @param {HTMLElement} element - La bulle .tyn-reply-bubble a desintegrer
 * @returns {Promise<void>} Resolve quand l'animation est terminee
 */
export function disintegrateElement(element) {
  return new Promise((resolve) => {
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      resolve();
      return;
    }

    const width = Math.ceil(rect.width);
    const height = Math.ceil(rect.height);

    // Canvas overlay en position fixed par-dessus l'element
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    canvas.style.cssText = `
      position: fixed;
      left: ${rect.left}px;
      top: ${rect.top}px;
      width: ${width}px;
      height: ${height}px;
      pointer-events: none;
      z-index: 99999;
    `;
    document.body.appendChild(canvas);
    const ctx = canvas.getContext("2d");

    // Extraire les couleurs source (image ou fond de bulle)
    let imageData = null;
    const img = element.querySelector("img");

    if (img && img.complete && img.naturalWidth > 0) {
      try {
        const srcCanvas = document.createElement("canvas");
        srcCanvas.width = width;
        srcCanvas.height = height;
        const srcCtx = srcCanvas.getContext("2d");

        // Dessiner le fond de la bulle d'abord
        const bgColor = getFallbackColor(element);
        srcCtx.fillStyle = bgColor;
        srcCtx.fillRect(0, 0, width, height);

        // Dessiner l'image par-dessus, positionnee relativement
        const imgRect = img.getBoundingClientRect();
        const offsetX = imgRect.left - rect.left;
        const offsetY = imgRect.top - rect.top;
        srcCtx.drawImage(img, offsetX, offsetY, imgRect.width, imgRect.height);

        imageData = srcCtx.getImageData(0, 0, width, height);
      } catch (e) {
        // CORS ou autre erreur, fallback couleur unie
        imageData = null;
      }
    }

    const fallbackColor = getFallbackColor(element);
    const fallbackParsed = imageData ? null : parseColor(fallbackColor);

    // Generer la grille de particules
    const cols = Math.ceil(width / PARTICLE_SIZE);
    const rows = Math.ceil(height / PARTICLE_SIZE);
    const particles = [];

    for (let col = 0; col < cols; col++) {
      for (let row = 0; row < rows; row++) {
        const x = col * PARTICLE_SIZE;
        const y = row * PARTICLE_SIZE;

        let r, g, b, a;
        if (imageData) {
          // Pixel central de la cellule
          const px = Math.min(x + 1, width - 1);
          const py = Math.min(y + 1, height - 1);
          const idx = (py * width + px) * 4;
          r = imageData.data[idx];
          g = imageData.data[idx + 1];
          b = imageData.data[idx + 2];
          a = imageData.data[idx + 3] / 255;
        } else {
          r = fallbackParsed.r;
          g = fallbackParsed.g;
          b = fallbackParsed.b;
          a = fallbackParsed.a;
        }

        // Ignorer les particules completement transparentes
        if (a < 0.05) continue;

        const delay =
          ((cols - col) / cols) * WAVE_DELAY + Math.random() * RANDOM_DELAY;

        // Vitesses avec biais vers l'exterieur (droite et haut)
        const angle = Math.random() * Math.PI * 2;
        const speed = 30 + Math.random() * 80;
        const vx = Math.cos(angle) * speed + (col / cols - 0.5) * 60;
        const vy = -Math.abs(Math.sin(angle) * speed) - 20 - Math.random() * 40;

        particles.push({
          x,
          y,
          originX: x,
          originY: y,
          vx,
          vy,
          r,
          g,
          b,
          a,
          delay,
          life: BASE_LIFE + Math.random() * LIFE_VARIANCE,
          size: PARTICLE_SIZE,
        });
      }
    }

    // Cacher l'element original (React unmount le composant via onDelete,
    // le canvas overlay sur document.body continue l'animation)
    element.style.opacity = "0";

    // Animation
    const startTime = performance.now();

    function animate(now) {
      const elapsed = now - startTime;
      ctx.clearRect(0, 0, width, height);

      let alive = false;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        const t = elapsed - p.delay;
        if (t < 0) {
          // Pas encore demarre, dessiner a la position d'origine
          ctx.globalAlpha = p.a;
          ctx.fillStyle = `rgb(${p.r},${p.g},${p.b})`;
          ctx.fillRect(p.originX, p.originY, p.size, p.size);
          alive = true;
          continue;
        }

        if (t > p.life) continue;

        alive = true;

        const tSec = t / 1000;
        const progress = t / p.life;

        const px = p.originX + p.vx * tSec;
        const py = p.originY + p.vy * tSec + 0.5 * GRAVITY * tSec * tSec;

        // Alpha decroissant + taille qui retrecit
        const alpha = p.a * (1 - progress);
        const size = p.size * (1 - progress * 0.5);

        if (alpha < 0.01) continue;

        ctx.globalAlpha = alpha;
        ctx.fillStyle = `rgb(${p.r},${p.g},${p.b})`;
        ctx.fillRect(px, py, size, size);
      }

      if (alive) {
        requestAnimationFrame(animate);
      } else {
        // Nettoyage
        canvas.remove();
        resolve();
      }
    }

    requestAnimationFrame(animate);
  });
}

/**
 * Recupere la couleur de fond de la bulle
 */
function getFallbackColor(element) {
  const textEl = element.querySelector(".tyn-reply-text");
  const target = textEl || element;
  const computed = getComputedStyle(target);
  const bg = computed.backgroundColor;
  if (bg && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent") {
    return bg;
  }
  // Remonter au parent .tyn-reply-bubble
  const bubble = element.closest(".tyn-reply-bubble") || element;
  const bubbleBg = getComputedStyle(bubble).backgroundColor;
  if (bubbleBg && bubbleBg !== "rgba(0, 0, 0, 0)" && bubbleBg !== "transparent") {
    return bubbleBg;
  }
  // Fallback : detecter si outgoing ou incoming
  const replyItem = element.closest(".tyn-reply-item");
  if (replyItem && replyItem.classList.contains("outgoing")) {
    return "#0156cd";
  }
  return "#6c757d";
}

/**
 * Parse une couleur CSS en composantes RGBA
 */
function parseColor(color) {
  if (color.startsWith("#")) {
    const hex = color.slice(1);
    return {
      r: parseInt(hex.substring(0, 2), 16),
      g: parseInt(hex.substring(2, 4), 16),
      b: parseInt(hex.substring(4, 6), 16),
      a: 1,
    };
  }
  const match = color.match(
    /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\)/
  );
  if (match) {
    return {
      r: parseInt(match[1]),
      g: parseInt(match[2]),
      b: parseInt(match[3]),
      a: match[4] !== undefined ? parseFloat(match[4]) : 1,
    };
  }
  return { r: 100, g: 100, b: 100, a: 1 };
}
